import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import { setQuery } from "../../store/slices/vacantesSlice.ts";
import { readBusinessNames } from "../../services/businessName.service.ts";
import { readBranchBySocialName } from "../../services/branch.service.ts";
import { notification } from "../../utils/Notifications.tsx";

interface IBusinessName {
  id: number;
  name: string;
}

interface IBranch {
  id: number;
  name: string;
}

const RESERVED_TYPE_VALUES = ["all", "true", "false"];

const getButtonStyle = (
  isSelected: boolean,
  isAll?: boolean
): React.CSSProperties => {
  if (isAll) {
    return {
      background: isSelected
        ? "linear-gradient(135deg, #374151 0%, #1f2937 100%)"
        : "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
      color: isSelected ? "#ffffff" : "#374151",
      border: "none",
      borderRadius: "16px",
      padding: "0.5rem 1rem",
      fontSize: "0.8rem",
      fontWeight: 500,
      minWidth: "100px",
      transition: "all 0.3s ease-in-out",
      boxShadow: isSelected
        ? "0 4px 12px rgba(55, 65, 81, 0.35)"
        : "0 2px 4px rgba(0, 0, 0, 0.08)",
      transform: isSelected ? "scale(1.05)" : "scale(1)",
      whiteSpace: "nowrap",
    };
  }
  return {
    background: isSelected
      ? "linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #dc2626 100%)"
      : "linear-gradient(135deg, #fef3c7 0%, #fed7aa 50%, #fee2e2 100%)",
    color: isSelected ? "#ffffff" : "#374151",
    borderRadius: "16px",
    padding: "0.45rem 1rem",
    minWidth: "120px",
    fontSize: "0.8rem",
    fontWeight: 500,
    border: "none",
    transform: isSelected ? "scale(1.03)" : "scale(1)",
    boxShadow: isSelected
      ? "0 4px 10px rgba(251, 191, 36, 0.4), 0 2px 4px rgba(220, 38, 38, 0.25)"
      : "0 2px 4px rgba(0, 0, 0, 0.08)",
    transition: "all 0.2s ease-in-out",
    whiteSpace: "nowrap",
  };
};

const FilterButtonsVacantes: React.FC = () => {
  const dispatch = useDispatch();
  const { query } = useSelector((state: RootState) => state.vacantes);
  const [businessNames, setBusinessNames] = React.useState<IBusinessName[]>([]);
  const [branches, setBranches] = React.useState<IBranch[]>([]);
  const [loadingBranches, setLoadingBranches] = React.useState(false);

  const params = React.useMemo(
    () => new URLSearchParams(query || ""),
    [query]
  );
  const typeParam = params.get("type") ?? "all";
  const selectedBranchId = params.get("branch") ?? "all";

  const isRazonSocialSelected =
    typeParam !== "all" && !RESERVED_TYPE_VALUES.includes(typeParam);
  const selectedBusinessName = React.useMemo(
    () => businessNames.find((bn) => bn.name === typeParam),
    [businessNames, typeParam]
  );

  const fetchBusinessNames = async () => {
    try {
      const list = await readBusinessNames();
      setBusinessNames(list || []);
    } catch (error) {
      notification("Error", "No se pudieron cargar las razones sociales", "danger");
    }
  };

  React.useEffect(() => {
    fetchBusinessNames();
  }, []);

  React.useEffect(() => {
    if (!selectedBusinessName) {
      setBranches([]);
      return;
    }
    let cancelled = false;
    setLoadingBranches(true);
    readBranchBySocialName(selectedBusinessName.id)
      .then((data) => {
        if (!cancelled) setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setBranches([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingBranches(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBusinessName?.id]);

  const sortedBusinessNames = React.useMemo(
    () =>
      [...businessNames].sort((a, b) =>
        a.name.localeCompare(b.name, "es")
      ),
    [businessNames]
  );

  const sortedBranches = React.useMemo(
    () => [...branches].sort((a, b) => a.name.localeCompare(b.name, "es")),
    [branches]
  );

  const buildQuery = (updates: { type?: string; branch?: string }) => {
    const next = new URLSearchParams(query || "");
    if (updates.type !== undefined) {
      if (updates.type === "all") next.delete("type");
      else next.set("type", updates.type);
    }
    if (updates.branch !== undefined) {
      if (updates.branch === "all") next.delete("branch");
      else next.set("branch", updates.branch);
    }
    return next.toString();
  };

  const handleRazonClick = (typeValue: string) => {
    const value = typeValue === "all" ? "all" : typeValue;
    dispatch(setQuery(buildQuery({ type: value, branch: "all" })));
  };

  const handleBranchClick = (branchId: string) => {
    dispatch(setQuery(buildQuery({ branch: branchId })));
  };

  if (sortedBusinessNames.length === 0) return null;

  return (
    <div className="card border-0 m-4 rounded">
      <div className="p-3">
        <div className="mb-2">
          <div
            className="d-flex flex-wrap align-items-center"
            style={{ gap: "0.5rem" }}
          >
            <button
              type="button"
              className="border-0 text-sm font-medium"
              style={getButtonStyle(typeParam === "all", true)}
              onClick={() => handleRazonClick("all")}
            >
              Todas
            </button>
            {sortedBusinessNames.map((bn) => {
              const isSelected = typeParam === bn.name;
              return (
                <button
                  key={bn.id}
                  type="button"
                  className="border-0 text-sm font-medium"
                  style={getButtonStyle(isSelected)}
                  onClick={() => handleRazonClick(bn.name)}
                >
                  {bn.name}
                </button>
              );
            })}
          </div>
        </div>

        {isRazonSocialSelected && (
          <div>
            <span className="text-muted small me-2"></span>
            <div
              className="d-flex flex-wrap align-items-center"
              style={{ gap: "0.5rem" }}
            >
              <button
                type="button"
                className="border-0 text-sm font-medium"
                style={getButtonStyle(selectedBranchId === "all", true)}
                onClick={() => handleBranchClick("all")}
              >
                Todas
              </button>
              {loadingBranches ? (
                <span className="small text-muted">
                  <i className="fa fa-spinner fa-spin me-1" />
                  Cargando sucursales...
                </span>
              ) : (
                sortedBranches.map((b) => {
                  const isSelected = selectedBranchId === String(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      className="border-0 text-sm font-medium"
                      style={getButtonStyle(isSelected)}
                      onClick={() => handleBranchClick(String(b.id))}
                    >
                      {b.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterButtonsVacantes;
